import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle2, Download, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { maskCPF, validaCPF } from "../lib/validators";
import { AssinaturaCanvas } from "../components/assinaturas/AssinaturaCanvas";
import { DECLARACAO, fmtBRL, fmtCPF, fmtDataHora } from "../components/assinaturas/assinaturasConst";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sign/proposta`;

const Marca = () => (
  <div className="text-xl font-display font-bold text-white" data-testid="sign-proposta-logo">
    Locar <span style={{ color: "#F97316" }}>Ativo</span> Brasil
  </div>
);

export default function AssinarPropostaPublica() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [erro, setErro] = useState(null);
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [aceite, setAceite] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/${token}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) return setErro(d.detail || "Link inválido");
        setInfo(d);
        setNome(d.nome || "");
        setCpf(fmtCPF(d.cpf));
        if (d.status === "assinado") setSucesso({ protocolo: d.protocolo, assinado_em: d.assinado_em });
      })
      .catch(() => setErro("Não foi possível carregar o documento"));
  }, [token]);

  const enviar = async () => {
    if (nome.trim().length < 3) return toast.error("Informe o nome completo");
    if (!validaCPF(cpf)) return toast.error("CPF inválido");
    if (canvasRef.current?.isEmpty()) return toast.error("Desenhe a assinatura no quadro");
    if (!aceite) return toast.error("Confirme a declaração antes de assinar");
    setSalvando(true);
    try {
      const r = await fetch(`${API}/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: nome.trim(), cpf, assinatura_base64: canvasRef.current.toDataURL(), confirmacao: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao assinar");
      setSucesso(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="sign-proposta-page">
      <header className="px-5 py-4" style={{ background: "#0f1923" }}><Marca /></header>
      <main className="max-w-xl mx-auto p-4 sm:p-6">
        {erro ? (
          <Card className="p-6 text-center space-y-2" data-testid="sign-proposta-erro">
            <AlertTriangle className="mx-auto text-amber-600" />
            <div className="font-medium">{erro}</div>
          </Card>
        ) : !info ? (
          <Loader2 className="animate-spin mx-auto mt-10 text-muted-foreground" />
        ) : sucesso ? (
          <Card className="p-6 text-center space-y-3" data-testid="sign-proposta-sucesso">
            <CheckCircle2 size={40} className="mx-auto text-green-600" />
            <div className="text-lg font-semibold">Assinatura registrada</div>
            <div className="text-sm text-muted-foreground">{fmtDataHora(sucesso.assinado_em)}</div>
            <div className="text-sm">Protocolo <strong data-testid="sign-proposta-protocolo">{sucesso.protocolo}</strong></div>
            <a href={`${API}/${token}/pdf`} data-testid="sign-proposta-download">
              <Button variant="outline" className="mt-2"><Download size={14} className="mr-1" /> Baixar PDF</Button>
            </a>
          </Card>
        ) : (
          <Card className="p-5 sm:p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Você foi convidado a assinar</div>
              <div className="text-xl font-display font-bold mt-1" style={{ color: "#1B3A6B" }} data-testid="sign-proposta-titulo">
                Proposta {info.proposta_numero}
              </div>
              <div className="text-sm text-muted-foreground">{info.cliente_nome}</div>
              <div className="text-sm mt-2 flex flex-wrap gap-x-4">
                <span>Valor: <strong>{fmtBRL(info.valor_total)}</strong></span>
                <span>Data: {new Date(info.data).toLocaleDateString("pt-BR")}</span>
              </div>
              <div className="mt-2 inline-block rounded-md px-2.5 py-1 text-xs font-semibold bg-slate-200" data-testid="sign-proposta-papel">
                Seu papel: {info.papel_label}
              </div>
            </div>
            <div>
              <Label>Nome completo</Label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} data-testid="sign-proposta-nome" />
            </div>
            <div>
              <Label>CPF</Label>
              <Input value={cpf} inputMode="numeric" onChange={(e) => setCpf(maskCPF(e.target.value))} placeholder="000.000.000-00" data-testid="sign-proposta-cpf" />
            </div>
            <div>
              <Label>Assinatura</Label>
              <AssinaturaCanvas ref={canvasRef} testId="sign-proposta-canvas" />
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={aceite} onCheckedChange={(v) => setAceite(!!v)} className="mt-0.5" data-testid="sign-proposta-aceite" />
              <span>{DECLARACAO[info.papel]}</span>
            </label>
            <Button className="w-full text-white" style={{ background: "#F97316" }} onClick={enviar} disabled={salvando} data-testid="sign-proposta-confirmar">
              {salvando ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShieldCheck size={14} className="mr-1" />}
              Confirmar e assinar
            </Button>
            <a href={`${API}/${token}/pdf`} className="block text-center text-xs underline text-slate-600" data-testid="sign-proposta-ver-pdf">
              Ler a proposta em PDF
            </a>
          </Card>
        )}
      </main>
    </div>
  );
}
